import React, { useState, useMemo } from "react";
import { useUserContext } from "../../context";
import { useApi } from "../../hooks";
import { Dialog, DialogBody, Typography, DialogHeader, Button, DialogFooter, IconButton } from "@material-tailwind/react";
import { ClockLoader } from "react-spinners";
import DatePicker from "../../components/date-picker";
import TimePicker from "../../components/time-picker";
import { HiMiniDocumentText } from "react-icons/hi2";


export default function Transactions() {

    const { fetchAccessToken, defaultHotel, user } = useUserContext();
    const { getPayments, getIncomes, updatePaymentStatuses, getImageUrl, getCard } = useApi();
    const [startDate, setStartDate] = useState(getFirstDayOfLastMonth());
    const [startTime, setStartTime] = useState("00:00");
    const [endDate, setEndDate] = useState(getLastDayOfLastMonth());
    const [endTime, setEndTime] = useState("23:59");
    const [isLoading, setIsLoading] = useState(false);
    const [totalPayments, setTotalPayments] = useState([]);
    const [totalIncomes, setTotalIncomes] = useState([]);
    const [selectedOption, setSelectedOption] = useState("All");
    const [paymentStatusMap, setPaymentStatusMap] = useState(new Map());
    const [incomeStatusMap, setIncomeStatusMap] = useState(new Map());
    const [isShowProofImage, setShowProofImage] = useState(false);
    const [imageUrls, setImageUrls] = useState([]);

    const paymentTotals = useMemo(() => {
        const allPayments = totalPayments || [];
        const allIncomes = totalIncomes || [];

        const paymentAll = allPayments.reduce((sum, payment) => sum + payment.paymentAmount, 0);
        const paymentUPI = allPayments.filter(p => p.paymentMode === "UPI").reduce((sum, payment) => sum + payment.paymentAmount, 0);
        const paymentCash = allPayments.filter(p => p.paymentMode === "Cash").reduce((sum, payment) => sum + payment.paymentAmount, 0);
        const paymentCard = allPayments.filter(p => p.paymentMode === "Card").reduce((sum, payment) => sum + payment.paymentAmount, 0);
        const paymentOthers = allPayments.filter(p => !["UPI", "Cash", "Card"].includes(p.paymentMode)).reduce((sum, payment) => sum + payment.paymentAmount, 0);

        const incomeAll = allIncomes.reduce((sum, income) => sum + income.incomeAmount, 0);
        const incomeUPI = allIncomes.filter(i => i.incomeType === "UPI").reduce((sum, income) => sum + income.incomeAmount, 0);
        const incomeCash = allIncomes.filter(i => i.incomeType === "Cash").reduce((sum, income) => sum + income.incomeAmount, 0);
        const incomeCard = allIncomes.filter(i => i.incomeType === "Card").reduce((sum, income) => sum + income.incomeAmount, 0);
        const incomeOthers = allIncomes.filter(i => !["UPI", "Cash", "Card"].includes(i.incomeType)).reduce((sum, income) => sum + income.incomeAmount, 0);

        const totalAll = paymentAll + incomeAll;
        const totalUPI = paymentUPI + incomeUPI;
        const totalCash = paymentCash + incomeCash;
        const totalCard = paymentCard + incomeCard;
        const totalOthers = paymentOthers + incomeOthers;

        return { totalAll, totalUPI, totalCash, totalCard, totalOthers };
    }, [totalPayments, totalIncomes]);

    const paymentOptions = [
        { label: 'All', value: paymentTotals.totalAll },
        { label: 'UPI', value: paymentTotals.totalUPI },
        { label: 'Cash', value: paymentTotals.totalCash },
        { label: 'Card', value: paymentTotals.totalCard },
        { label: 'Others', value: paymentTotals.totalOthers }
    ];

    const filteredPayments = useMemo(() => {
        if (selectedOption === "All") {
            return totalPayments;
        }

        return totalPayments
            .filter(payment => {
                if (selectedOption === "Others") {
                    return !["UPI", "Cash", "Card"].includes(payment.paymentMode);
                }
                return payment.paymentMode === selectedOption;
            });

    }, [totalPayments, selectedOption]);

    const filteredIncomes = useMemo(() => {
        if (selectedOption === "All") {
            return totalIncomes;
        }

        return totalIncomes.filter(income => {
            if (selectedOption === "Others") {
                return !["UPI", "Cash", "Card"].includes(income.incomeType);
            }
            return income.incomeType === selectedOption;
        });
    }, [totalIncomes, selectedOption]);

    const handlePaymentStatusChange = (paymentId, field, checked) => {
        setPaymentStatusMap(prev => {
            const newMap = new Map(prev);
            const currentStatus = newMap.get(paymentId) || { verified: false, fault: false };
            newMap.set(paymentId, { ...currentStatus, [field]: checked });
            return newMap;
        });
    };

    const handleIncomeStatusChange = (incomeId, field, checked) => {
        setIncomeStatusMap(prev => {
            const newMap = new Map(prev);
            const currentStatus = newMap.get(incomeId) || { verified: false, fault: false };
            newMap.set(incomeId, { ...currentStatus, [field]: checked });
            return newMap;
        });
    };

    const syncStatusToCloud = async () => {
        if (!defaultHotel) {
            return;
        }
        try {
            setIsLoading(true);
            const token = await fetchAccessToken();

            const paymentUpdates = Array.from(paymentStatusMap.entries()).map(([paymentId, status]) => ({
                paymentId: paymentId,
                verified: status.verified,
                fault: status.fault
            }));

            const incomeUpdates = Array.from(incomeStatusMap.entries()).map(([incomeId, status]) => ({
                incomeId: incomeId,
                verified: status.verified,
                fault: status.fault
            }));

            const updatePaymentStatusesRequest = {
                hotelId: defaultHotel,
                updatedBy: user.name,
                paymentUpdates: paymentUpdates,
                incomeUpdates: incomeUpdates
            };
            await updatePaymentStatuses(defaultHotel, updatePaymentStatusesRequest, token);
            alert('Status synced to cloud successfully.');
            setPaymentStatusMap(new Map());
            setIncomeStatusMap(new Map());
            getTransactionsInternal();
        } catch (ex) {
            alert('Failed to sync status to cloud.');
            console.error(ex);
        } finally {
            setIsLoading(false);
        }
    };


    const getTransactionsInternal = async () => {
        if (!defaultHotel) {
            return;
        }
        try {
            setIsLoading(true);
            const start = getEpoch(startDate, startTime);
            const end = getEpoch(endDate, endTime);
            const token = await fetchAccessToken();
            const paymentsData = await getPayments(defaultHotel, start, end, token);
            if (paymentsData && paymentsData.length > 0) {
                setTotalPayments(paymentsData.sort((a, b) => a.paymentDateTime - b.paymentDateTime));
            } else {
                setTotalPayments([]);
            }
            const incomesData = await getIncomes(defaultHotel, start, end, token);
            if (incomesData && incomesData.length > 0) {
                setTotalIncomes(incomesData.sort((a, b) => a.incomeDateTime - b.incomeDateTime));
            }
            else {
                setTotalIncomes([]);
            }
            setSelectedOption("All");
        } catch (ex) {
            alert("Something went wrong.")
        } finally {
            setIsLoading(false);
        }

    };

    function getEpoch(date, time, seconds = 0) {
        const [hours, minutes] = time.split(':').map(Number);
        const combinedDate = new Date(date);
        combinedDate.setHours(hours);
        combinedDate.setMinutes(minutes);
        combinedDate.setSeconds(seconds);
        combinedDate.setMilliseconds(seconds * 100);

        return combinedDate.getTime();
    }


    function getFirstDayOfLastMonth() {
        const now = new Date();
        return now;
    }

    function getLastDayOfLastMonth() {
        const now = new Date();
        return now;
    }

    let handleCardNoClick = async (cardNo) => {
        if (cardNo) {
            try {
                setIsLoading(true);
                const token = await fetchAccessToken();
                const card = await getCard(defaultHotel, cardNo, token);
                if (card && card.documentS3Ids && card.documentS3Ids.length > 0) {
                    await handleProofView(card.documentS3Ids);
                } else {
                    alert("No proof documents found.");
                }
            } catch (ex) {
                alert("Error occurred while searching for card proofs.");
            } finally {
                setIsLoading(false);
            }
        }
    }

    let handleProofView = async (documentS3Ids) => {
        try {
            const token = await fetchAccessToken();
            const imageUrlsTemp = [];

            for (const id of documentS3Ids) {
                const url = await getImageUrl(id, token);
                if (url) {
                    imageUrlsTemp.push(url);
                }
            }

            if (imageUrlsTemp.length > 0) {
                setImageUrls(imageUrlsTemp);
                setShowProofImage(true);
            }
        } catch (ex) {
            alert("Error occurred while viewing the payment Proofs.");
        }
    };

    return (
        <>
            <div className="p-6">
                <h1 className="text-2xl font-bold mb-4">Transactions</h1>

                <div className="mb-6">
                    <div className="overflow-x-auto">
                        <div className="flex gap-3 pb-2 min-w-max">
                            {paymentOptions.map((option) => (
                                <div
                                    key={option.label}
                                    className={`flex-shrink-0 p-3 border rounded-lg cursor-pointer transition-colors min-w-[120px] ${selectedOption === option.label
                                        ? 'bg-blue-100 border-blue-500'
                                        : 'bg-white border-gray-300 hover:bg-gray-50'
                                        }`}
                                    onClick={() => setSelectedOption(option.label)}
                                >
                                    <div className="text-xs text-gray-600 text-center">{option.label}</div>
                                    <div className="text-sm font-semibold text-center">₹{option.value.toLocaleString()}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap">
                    <div className="w-full lg:w-4/12 px-4">
                        <div className="h-10 flex justify-between sm:justify-start">
                            <Typography className="mt-2">
                                Start:
                            </Typography>
                            <div className="flex flex-row">
                                <DatePicker defaultDate={startDate} onDateSelect={setStartDate} />
                                <TimePicker defaultTime={startTime} onTimeChange={setStartTime} />
                            </div>
                        </div>
                    </div>
                    <div className="w-full lg:w-4/12 px-4">
                        <div className="h-10 flex justify-between sm:justify-start">
                            <Typography className="mt-2">
                                End:
                            </Typography>
                            <div className="flex flex-row">
                                <DatePicker defaultDate={endDate} onDateSelect={setEndDate} />
                                <TimePicker defaultTime={endTime} onTimeChange={setEndTime} />
                            </div>
                        </div>
                    </div>
                    <div className="w-full sm:w-4/12 p-1">
                        <div className="h-10 flex justify-end gap-2">
                            <button className="bg-pink-500 text-white hover:bg-pink-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
                                type="button" onClick={getTransactionsInternal}>
                                Get
                            </button>
                        </div>
                    </div>
                </div>

                <div className="mb-6">
                    <div className="grid gap-4">
                        {filteredPayments
                            .map((payment, index) => {
                                const paymentId = payment.paymentDateTime;
                                const currentStatus = paymentStatusMap.get(paymentId) || { verified: false, fault: false };
                                const isVerified = payment.verified;
                                const isFault = payment.fault;

                                return (
                                    <div key={index} className={`bg-white border border-gray-300 rounded-lg p-4 hover:shadow-md transition-shadow ${isVerified ? 'line-through' : ''} ${isFault ? 'text-red-600' : ''}`}>
                                        <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
                                            <div>
                                                <label className="text-sm font-medium text-gray-600">S.No</label>
                                                <p className="text-sm font-semibold">{index + 1}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-600">Card No</label>
                                                <p className="text-sm font-semibold">{payment.cardNo}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-600">Payment Mode</label>
                                                <p className="text-sm font-semibold">{payment.paymentMode}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-600">Amount</label>
                                                <p className="text-sm font-semibold">₹{payment.paymentAmount.toLocaleString()}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-600">Date & Time</label>
                                                <p className="text-sm font-semibold">
                                                    {new Date(payment.paymentDateTime).toLocaleString()}
                                                </p>
                                            </div>
                                            <div>
                                                    <a href="#" className="cursor-pointer text-blue-600 underline" style={{ textDecoration: "underline" }} onClick={(e) => {
                                                        e.preventDefault();
                                                        handleCardNoClick(payment.cardNo);
                                                    }} >Proofs</a>
                                            </div>
                                            {!isVerified && !isFault && (
                                                <>
                                                    <div>
                                                        <label className="text-sm font-medium text-gray-600 mr-2 mt-2">Verified</label>
                                                        <input
                                                            type="checkbox"
                                                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                            checked={currentStatus.verified}
                                                            onChange={(e) => handlePaymentStatusChange(paymentId, 'verified', e.target.checked)}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium text-gray-600  mr-2 mt-2">Missing</label>
                                                        <input
                                                            type="checkbox"
                                                            className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                                                            checked={currentStatus.fault}
                                                            onChange={(e) => handlePaymentStatusChange(paymentId, 'fault', e.target.checked)}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )
                            })
                        }
                    </div>
                </div>            <div>
                    <div className="grid gap-4">
                        {filteredIncomes
                            .map((income, index) => {
                                const incomeId = income.incomeDateTime;
                                const currentStatus = incomeStatusMap.get(incomeId) || { verified: false, fault: false };
                                const isVerified = income.verified;
                                const isFault = income.fault;

                                return (
                                    <div key={index} className={`bg-yellow-100 border border-gray-300 rounded-lg p-4 hover:shadow-md transition-shadow ${isVerified ? 'line-through' : ''} ${isFault ? 'text-red-600' : ''}`}>
                                        <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
                                            <div>
                                                <label className="text-sm font-medium text-gray-600">S.No</label>
                                                <p className="text-sm font-semibold">{filteredPayments.length + index + 1}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-600">Card Nos</label>
                                                <p className="text-sm font-semibold break-words">{income.cardNos.join(', ')}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-600">Income Type</label>
                                                <p className="text-sm font-semibold">{income.incomeType}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-600">Amount</label>
                                                <p className="text-sm font-semibold">₹{income.incomeAmount.toLocaleString()}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-600">Date & Time</label>
                                                <p className="text-sm font-semibold">
                                                    {new Date(income.incomeDateTime).toLocaleString()}
                                                </p>
                                            </div>
                                            <div>{" "}</div>
                                            {!isVerified && !isFault && (
                                                <>
                                                    <div>
                                                        <label className="text-sm font-medium text-gray-600 mr-2 mt-2">Verified</label>
                                                        <input
                                                            type="checkbox"
                                                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                            checked={currentStatus.verified}
                                                            onChange={(e) => handleIncomeStatusChange(incomeId, 'verified', e.target.checked)}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium text-gray-600 mr-2 mt-2">Missing</label>
                                                        <input
                                                            type="checkbox"
                                                            className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                                                            checked={currentStatus.fault}
                                                            onChange={(e) => handleIncomeStatusChange(incomeId, 'fault', e.target.checked)}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )
                            })
                        }
                    </div>
                </div>
                <Dialog open={isLoading} className="bg-transparent shadow-none" size="md">
                    <DialogBody className="bg-transparent shadow-none flex justify-center">
                        <ClockLoader color="#2be60d" />
                    </DialogBody>
                </Dialog>
                <Dialog size={"xs"} open={isShowProofImage}>
                    <DialogHeader className="flex justify-end items-center h-full">
                        <Button
                            variant="text"
                            color="red"
                            onClick={() => {
                                setShowProofImage(false);
                                setImageUrls([]);
                            }}
                            className="mr-1"
                        >
                            <span>Close</span>
                        </Button>
                    </DialogHeader>
                    <DialogBody>
                        <div className="max-h-[500px] overflow-y-auto space-y-4">
                            {imageUrls.map((url, index) => (
                                <img
                                    key={index}
                                    src={url}
                                    alt={`Proof ${index + 1}`}
                                    className="w-full rounded shadow"
                                />
                            ))}
                        </div>
                    </DialogBody>
                    <DialogFooter>
                        <Button
                            variant="text"
                            color="red"
                            onClick={() => {
                                setShowProofImage(false);
                                setImageUrls([]);
                            }}
                            className="mr-1"
                        >
                            <span>Close</span>
                        </Button>
                    </DialogFooter>
                </Dialog>
            </div>

            {/* Fixed Sync Status Button - Outside main container */}
            <button
                className="bg-green-500 text-white hover:bg-green-600 font-bold uppercase text-sm px-6 py-3 rounded-full shadow-lg hover:shadow-xl outline-none focus:outline-none ease-linear transition-all duration-150"
                type="button"
                onClick={syncStatusToCloud}
                disabled={paymentStatusMap.size === 0 && incomeStatusMap.size === 0}
                style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    zIndex: 10000,
                    display: 'block'
                }}
            >
                Sync Status
            </button>
        </>
    );
}